/* useTenantTheme — applies the tenant's accent color + typeface pair as
   document-level CSS variables, so every Phase 5 template can read them
   uniformly via `var(--tenant-accent) / --tenant-serif / --tenant-sans`.

   Inputs are read straight from `useSiteSettings()` — no network call here.
   • accent  ← settings.theme_overrides.primary  ||  '#C9A961' (gold fallback)
   • pair    ← settings.typeface_pair             ||  'cormorant-manrope'

   Variables set on `:root`:
     --tenant-accent       solid hex
     --tenant-accent-soft  10% alpha of accent (for tile backgrounds)
     --tenant-accent-line  35% alpha of accent (for hairlines)
     --tenant-serif        full font-family stack
     --tenant-sans         full font-family stack
*/

import { useEffect } from "react";
import { useSiteSettings } from "./useSiteSettings";
import { resolvePalette } from "@/lib/themes";

const PAIRS = {
  "cormorant-manrope": {
    serif: "'Cormorant Garamond', 'EB Garamond', Georgia, serif",
    sans:  "'Manrope', system-ui, -apple-system, 'Helvetica Neue', sans-serif",
  },
  "canela-gtamerica": {
    serif: "'Canela', 'Cormorant Garamond', 'Didot', 'Bodoni 72', serif",
    sans:  "'GT America', 'Inter', -apple-system, system-ui, sans-serif",
  },
  "ppeditorial-sohne": {
    serif: "'PP Editorial New', 'Cormorant Garamond', 'Times New Roman', serif",
    sans:  "'Söhne', 'Söhne Buch', 'Inter', -apple-system, sans-serif",
  },
  // ── New pairs for the Atelier/Heirloom/Vista/Brutalist/Botanical/Spotlight
  // template variants. All families are loaded in public/index.html. ────────
  "playfair-inter": {
    // Playfair Display — high-contrast didone serif. Used by Atelier
    // (intimate/boudoir) for the romantic, sensual editorial mood, and by
    // Vista (landscape/travel) for the open, magazine-cover feel.
    serif: "'Playfair Display', 'Cormorant Garamond', 'Didot', serif",
    sans:  "'Inter', 'Manrope', -apple-system, system-ui, sans-serif",
  },
  "fraunces-manrope": {
    // Fraunces — soft, organic display serif with botanical curves.
    // Used by Botanical (verdant/earthy) for that "garden-press"
    // hand-typeset feel.
    serif: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
    sans:  "'Manrope', system-ui, -apple-system, sans-serif",
  },
  "jetbrains-inter": {
    // JetBrains Mono — pure utilitarian monospace. Used by Brutalist
    // (grid-strict / no-decoration) so studio names and captions read as
    // technical specifications rather than calligraphy.
    serif: "'JetBrains Mono', ui-monospace, 'SFMono-Regular', Menlo, monospace",
    sans:  "'Inter', 'Manrope', -apple-system, system-ui, sans-serif",
  },
  "dmserif-inter": {
    // DM Serif Display — dramatic theatrical didone with heavy contrast.
    // Used by Spotlight (cinematic dark) for the poster-headline effect.
    serif: "'DM Serif Display', 'Playfair Display', 'Didot', serif",
    sans:  "'Inter', 'Manrope', -apple-system, system-ui, sans-serif",
  },
};

const GOLD = "#C9A961";

function hexToRgb(hex) {
  if (!hex || typeof hex !== "string") return null;
  let h = hex.replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

export function useTenantTheme() {
  const { settings } = useSiteSettings();
  useEffect(() => {
    // The admin Branding picker writes one of three places:
    //   1. `theme_id` — a curated theme ("editorial-gold", "midnight-violet", ...)
    //   2. `theme_overrides` — per-color overrides on top of (1)
    //   3. `accent_color` — legacy single-field accent on older studios
    // We resolve in that exact precedence: overrides win, then curated theme
    // palette, then accent_color fallback, then the gold default. Mirrors
    // `resolvePalette` in @/lib/themes so the admin live-preview and the
    // public tenant pages paint the same color.
    // The theme schema has BOTH `primary` (CTA fill) and `accent` (italic
    // word + hairlines + eyebrows). For the public template's "gold word"
    // accent we want `accent` first, then fall back through overrides /
    // primary / accent_color / gold default. Mirrors Allure-image.com's
    // distinction: dark CTA fills, orange italic accents.
    const resolved = resolvePalette(settings?.theme_id, settings?.theme_overrides);
    const accent =
      settings?.theme_overrides?.accent
      || settings?.theme_overrides?.primary
      || resolved?.accent
      || resolved?.primary
      || settings?.accent_color
      || GOLD;
    const accentFg =
      settings?.theme_overrides?.primary_foreground
      || resolved?.primary_foreground
      || "#FFFFFF";
    // Canvas/text colors from the same resolved palette. Templates that paint
    // their own surface (Editorial = dark, Minimal = cream, etc.) read these
    // through CSS vars so a theme swap reshapes the whole site, not just the
    // accent word. Falls back to a stone palette if the theme is malformed.
    const bg  = settings?.theme_overrides?.background || resolved?.background  || "#0A0908";
    const fg  = settings?.theme_overrides?.foreground || resolved?.foreground  || "#F5F2EC";
    const muted = settings?.theme_overrides?.muted    || resolved?.muted       || "#15120F";

    const pair = PAIRS[settings?.typeface_pair] || PAIRS["cormorant-manrope"];
    const rgb = hexToRgb(accent) || hexToRgb(GOLD);
    const fgRgb = hexToRgb(fg);
    const bgRgb = hexToRgb(bg);
    const r = document.documentElement.style;
    r.setProperty("--tenant-accent", accent);
    r.setProperty("--tenant-accent-fg", accentFg);
    r.setProperty("--tenant-bg", bg);
    r.setProperty("--tenant-fg", fg);
    r.setProperty("--tenant-muted", muted);
    if (rgb) {
      r.setProperty("--tenant-accent-soft", `rgba(${rgb.join(",")}, 0.10)`);
      r.setProperty("--tenant-accent-line", `rgba(${rgb.join(",")}, 0.35)`);
      r.setProperty("--tenant-accent-rgb", rgb.join(","));
    }
    if (fgRgb) {
      r.setProperty("--tenant-fg-rgb", fgRgb.join(","));
      r.setProperty("--tenant-fg-soft", `rgba(${fgRgb.join(",")}, 0.65)`);
      r.setProperty("--tenant-fg-mute", `rgba(${fgRgb.join(",")}, 0.45)`);
      r.setProperty("--tenant-fg-line", `rgba(${fgRgb.join(",")}, 0.15)`);
    }
    if (bgRgb) {
      // Tinted overlay for the full-bleed editorial hero. We bleed the canvas
      // color (alpha-stepped) over the image so a LIGHT theme produces a soft
      // cream/paper wash (the hero text uses --tenant-fg which is dark on
      // light themes) and a DARK theme produces the original cinematic black
      // wash. No JS branching needed in the template.
      r.setProperty("--tenant-bg-rgb", bgRgb.join(","));
      r.setProperty("--tenant-hero-overlay", `linear-gradient(to bottom, rgba(${bgRgb.join(",")}, 0.40) 0%, rgba(${bgRgb.join(",")}, 0.30) 40%, rgba(${bgRgb.join(",")}, 0.85) 100%)`);
      r.setProperty("--tenant-hero-vignette", `linear-gradient(to right, rgba(${bgRgb.join(",")}, 0.55) 0%, rgba(${bgRgb.join(",")}, 0) 50%)`);
    }
    r.setProperty("--tenant-serif", pair.serif);
    r.setProperty("--tenant-sans", pair.sans);
    // Cleanup intentionally left out — see comment below.
    return () => {
      // Intentionally do NOT remove the vars: returning to admin/marketing
      // still benefits from a consistent gold accent; the admin shell uses
      // var(--gold-base) explicitly so there's no collision.
    };
  }, [
    settings?.theme_id,
    settings?.theme_overrides,
    settings?.theme_overrides?.primary,
    settings?.theme_overrides?.primary_foreground,
    settings?.theme_overrides?.accent,
    settings?.theme_overrides?.background,
    settings?.theme_overrides?.foreground,
    settings?.theme_overrides?.muted,
    settings?.accent_color,
    settings?.typeface_pair,
  ]);
}

export function getSeason(date = new Date()) {
  const m = date.getMonth();
  if (m <= 1 || m === 11) return "Winter";
  if (m <= 4) return "Spring";
  if (m <= 7) return "Summer";
  return "Autumn";
}
